import { useSourceLanguage } from "../i18n/LocalizedCopy";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import React from "react";
import { imageForFeature } from "./rubricImages";
import { Link } from "react-router-dom";

import { useFeatureRegistry } from "./FeatureRegistryContext";
import { FeatureCard } from "./FeatureStatusUI";
import { RegistrySourceNote, TodayPaymentNotice } from "./RegistryState";
import { getTodayCatalog } from "./todayCatalog";
import "./EternuaOrientation.css";
import "./HomeCatalogPremium.css";

const VISION_TEMPLE_KEYS = [
  "vision-france-3024",
  "vision-human-ai",
  "vision-lia-evolution",
  "vision-et-civilizations",
  "vision-eternal-health",
  "vision-temples",
  "vision-infrastructures",
  "vision-dossiers",
];

export default function EternuaOrientation({ compact = false, homePresentation = false }) {
  useSourceLanguage();
  const { translateLabel, translateMessage } = useNavigationTranslations();
  const { today, vision, source, refresh } = useFeatureRegistry();
  const catalog = getTodayCatalog(source === "server" ? today : []);
  const todayTemple = catalog.home;
  const visionTemple = VISION_TEMPLE_KEYS
    .map((featureKey) => vision.find((feature) => feature.featureKey === featureKey))
    .filter(Boolean);

  return (
    <div className={`eternua-orientation${compact ? " eternua-orientation--compact" : ""}`}>
      <section className="eternua-orientation__space eternua-orientation__space--today" aria-labelledby="eternua-today-title">
        <span className="eternua-orientation__section-number" aria-hidden="true">01</span>
        <div className="eternua-orientation__space-heading">
          <div className="eternua-orientation__heading-copy">
            <div className="eternua-orientation__eyebrow">
              <span className="eternua-orientation__eyebrow-sigil" aria-hidden="true">◉</span>
              <span>{translateLabel("Aujourd’hui")}</span>
              <i aria-hidden="true" />
              {source === "server" ? <small>{translateMessage(todayTemple.length === 1 ? "navigation.orientation.services_one" : "navigation.orientation.services_many", `${todayTemple.length} service${todayTemple.length > 1 ? "s" : ""} actif${todayTemple.length > 1 ? "s" : ""} ou en bêta`, { count: todayTemple.length })}</small> : null}
            </div>
            <h2 id="eternua-today-title">{translateLabel(homePresentation ? "Eternua aujourd’hui" : "Utiliser Eternua aujourd’hui")}</h2>
            <p>{translateLabel("Acheter, vendre, travailler, organiser et échanger : retrouvez les services déclarés actifs ou en bêta, avec leurs capacités et leurs limites.")}</p>
          </div>
          <Link className="eternua-orientation__section-action" to="/aujourdhui">
            <span>
              <small>{translateLabel("Accès direct")}</small>
              <strong>{translateLabel("Voir l’espace Aujourd’hui")}</strong>
            </span>
            <i aria-hidden="true">↗</i>
          </Link>
        </div>
        <RegistrySourceNote source={source} onRetry={refresh} />
        <TodayPaymentNotice />
        <div className="eternua-orientation__grid">
          {todayTemple.map((feature) => (
            <FeatureCard feature={feature} photo={imageForFeature(feature)?.src} photoAlt={imageForFeature(feature)?.alt} compact={compact} presentation="temple" key={feature.featureKey} />
          ))}
        </div>
        {source === "server" && !todayTemple.length ? <p className="today-catalog-empty">{translateLabel("Aucun service actif ou en bêta n’est actuellement proposé dans le registre.")}</p> : null}
        {source === "server" ? (
          <nav className="today-catalog-links" aria-label={translateLabel("Autres statuts Aujourd’hui")}>
            {catalog.readOnly.length ? <Link to="/aujourdhui#lecture-seule">{translateMessage("navigation.orientation.read_only", `${catalog.readOnly.length} en lecture seule`, { count: catalog.readOnly.length })}</Link> : null}
            {catalog.privateBeta.length ? <Link to="/aujourdhui#acces-limite">{translateMessage("navigation.orientation.private_beta", `${catalog.privateBeta.length} en bêta privée`, { count: catalog.privateBeta.length })}</Link> : null}
            {catalog.upcoming.length ? <Link to="/aujourdhui#en-preparation">{translateMessage("navigation.orientation.upcoming", `${catalog.upcoming.length} en préparation ou fermés`, { count: catalog.upcoming.length })}</Link> : null}
          </nav>
        ) : null}
      </section>

      <div className="eternua-orientation__divider" role="separator" aria-label={translateLabel("Séparation entre usages actuels et vision prospective")}>
        <span aria-hidden="true"><i>∞</i></span>
      </div>

      <section className="eternua-orientation__space eternua-orientation__space--vision" aria-labelledby="eternua-vision-title">
        <span className="eternua-orientation__section-number" aria-hidden="true">3024</span>
        <div className="eternua-orientation__space-heading">
          <div className="eternua-orientation__heading-copy">
            <div className="eternua-orientation__eyebrow">
              <span className="eternua-orientation__eyebrow-sigil" aria-hidden="true">✦</span>
              <span>{translateLabel(homePresentation ? "Vision · 2035–3024" : "Prospective · 3024")}</span>
              <i aria-hidden="true" />
              <small>{translateLabel("Temple éditorial")}</small>
            </div>
            <h2 id="eternua-vision-title">{translateLabel(homePresentation ? "Eternua demain" : "Découvrir la Vision 3024")}</h2>
            <p>
              {translateLabel(homePresentation
                ? "La vision 2035–3024 ouvre un horizon pour Aelith, les IA humaines, les temples et les infrastructures d’Eternua. Ce sont des projets, des recherches et des récits à explorer, distincts des services disponibles aujourd’hui."
                : "La Vision 3024 rassemble les recherches, récits et projections à long terme d’Eternua. Ces contenus relèvent d’un horizon éditorial et prospectif, distinct des usages actuels.")}
            </p>
          </div>
          <Link className="eternua-orientation__section-action" to="/vision-3024">
            <span>
              <small>{translateLabel("Horizon éditorial")}</small>
              <strong>{translateLabel("Entrer dans la Vision 3024")}</strong>
            </span>
            <i aria-hidden="true">↗</i>
          </Link>
        </div>
        <div className="eternua-orientation__notice" role="note">
          <span className="eternua-orientation__notice-sigil" aria-hidden="true">◇</span>
          <div>
            <strong>{translateLabel("À lire comme une vision.")}</strong>
            <span>{translateLabel("Aucune carte de cet espace ne constitue une promesse de réalisation, une preuve, ni une date de lancement.")}</span>
          </div>
        </div>
        <div className="eternua-orientation__grid">
          {visionTemple.map((feature) => (
            <FeatureCard feature={feature} photo={imageForFeature(feature)?.src} photoAlt={imageForFeature(feature)?.alt} compact={compact} presentation="temple" key={feature.featureKey} />
          ))}
        </div>
      </section>
    </div>
  );
}
